import { Currency, isMoneyString } from "@ibada/shared-types/finance"

import { http } from "@/shared/lib/api/http"
import type { CurrencyCode } from "@/features/finance/model/finance-types"
import type {
  CreateReconciliationSessionInput,
  FiscalCalendar,
  FiscalYear,
  ListReconciliationSessionsQuery,
  OpeningBalancesQuery,
  OpeningBalancesState,
  ReconciliationSessionDetail,
  ReconciliationSessionListResponse,
  ReconciliationSessionStatus,
  ReconciliationSessionSummary,
  ReconciliationTransitionInput,
} from "@/features/finance/model/finance-close-trust-types"
import type { FinancePaymentMethod } from "@/features/finance/model/finance-types"
import { ReconciliationLineState, ReconciliationViewerAction } from "@/features/finance/model/finance-close-trust-types"
import type { ReconciliationOpeningCheck } from "@/features/finance/model/finance-close-trust-types"

/** Route segment per transition. Kept beside the caller so the two can never disagree. */
const TRANSITION_PATH: Record<ReconciliationViewerAction, string> = {
  [ReconciliationViewerAction.Submit]: "submit",
  [ReconciliationViewerAction.Approve]: "approve",
  [ReconciliationViewerAction.Reject]: "reject",
  [ReconciliationViewerAction.WithdrawToDraft]: "withdraw",
}

/**
 * API client for Batch 5 (Close & Trust the Books): fiscal periods (IBA-72), opening balances (IBA-71), and
 * account reconciliation (IBA-73).
 *
 * Money arrives as a canonical decimal string (IBA-116) and STAYS one — a reconciliation whose statement
 * balance has been through a float is a reconciliation that can silently fail to reconcile. Only genuine
 * numbers (the fiscal start month, a line count) are coerced.
 */

/** For genuine integers only — a month index, a count. Never money. */
const count = (value: unknown): number => {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

/** Money stays a decimal string. `"0"` is valid money in every currency, and `Intl` prints it at the right scale. */
const money = (value: unknown): string => {
  if (typeof value === "string" && isMoneyString(value.trim())) return value.trim()
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  return "0"
}

const nullableMoney = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : money(value)

/**
 * The currency a money string is denominated in, as the API sent it (IBA-116).
 *
 * Null when it is absent or unknown — NOT "USD". Guessing the scale is precisely the bug: it is what would print
 * a whole Rwandan franc as a dollar with cents.
 */
const currencyCode = (value: unknown): CurrencyCode | null => {
  if (value === Currency.CDF) return Currency.CDF
  if (value === Currency.EUR) return Currency.EUR
  if (value === Currency.RWF) return Currency.RWF
  if (value === Currency.USD) return Currency.USD
  return null
}

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === "object" ? (value as Record<string, unknown>) : {})

function scopeParams(query: { workspaceId: string; branchId?: string | null; scope?: string }) {
  return {
    workspaceId: query.workspaceId,
    ...(typeof query.branchId !== "undefined" && query.branchId !== null ? { branchId: query.branchId } : {}),
    ...(query.scope ? { scope: query.scope } : {}),
  }
}

/* --- Fiscal periods (IBA-72) --- */

async function getFiscalCalendar(workspaceId: string, branchId: string): Promise<FiscalCalendar> {
  // The calendar belongs to the BRANCH (IBA-118) — its months are cut in the branch's timezone, so asking for
  // "the workspace's calendar" is not a question with one answer.
  const response = await http.get<unknown>("/finance/fiscal/calendar", { params: { workspaceId, branchId } })
  const record = asRecord(response.data)
  return {
    fiscalYearStartMonth: count(record.fiscalYearStartMonth) || 1,
    startMonthEditable: Boolean(record.startMonthEditable),
    canClosePeriods: record.canClosePeriods === true,
    years: Array.isArray(record.years)
      ? (record.years as FiscalYear[]).map((year) => ({
          ...year,
          periods: (Array.isArray(year.periods) ? year.periods : []).map((period) => ({
            ...period,
            entryCount: count(period.entryCount),
          })),
        }))
      : [],
  }
}

async function closeFiscalPeriod(input: { workspaceId: string; branchId: string; periodId: string }): Promise<void> {
  await http.post<unknown>("/finance/fiscal/periods/close", input)
}

async function reopenFiscalPeriod(input: { workspaceId: string; branchId: string; periodId: string }): Promise<void> {
  await http.post<unknown>("/finance/fiscal/periods/reopen", input)
}

async function updateFiscalSettings(input: {
  workspaceId: string
  branchId: string
  fiscalYearStartMonth: number
}): Promise<void> {
  await http.put<unknown>("/finance/fiscal/settings", input)
}

/* --- Opening balances (IBA-71) --- */

async function getOpeningBalancesState(query: OpeningBalancesQuery): Promise<OpeningBalancesState> {
  const response = await http.get<unknown>("/finance/opening-balances/state", { params: scopeParams(query) })
  return normalizeOpeningBalancesState(response.data)
}

/** One shape, whether it arrives from the GET or comes back from a write. */
function normalizeOpeningBalancesState(data: unknown): OpeningBalancesState {
  const record = asRecord(data)
  const accounts = Array.isArray(record.accounts) ? record.accounts : []
  return {
    branchId: String(record.branchId ?? ""),
    // Do not invent a currency: leave it empty when the backend omits it and let the branch currency stand in.
    baseCurrency: record.baseCurrency ? String(record.baseCurrency) : "",
    openingDate: (record.openingDate as string | null) ?? null,
    initialized: Boolean(record.initialized),
    funds: Array.isArray(record.funds) ? (record.funds as OpeningBalancesState["funds"]) : [],
    accounts: accounts.map((account) => {
      const a = asRecord(account)
      return {
        id: String(a.id ?? ""),
        name: String(a.name ?? ""),
        set: Boolean(a.set),
        openingBalance: nullableMoney(a.openingBalance),
        fundId: (a.fundId as string | null) ?? null,
        fundName: (a.fundName as string | null) ?? null,
      }
    }),
  }
}

/* --- Account reconciliation (IBA-73) --- */

function normalizeSessionSummary(value: unknown): ReconciliationSessionSummary {
  const r = asRecord(value)
  const account = asRecord(r.financeAccount)
  const counts = asRecord(r._count)
  return {
    id: String(r.id ?? ""),
    financeAccount: { id: String(account.id ?? ""), name: String(account.name ?? "") },
    statementDateFrom: String(r.statementDateFrom ?? ""),
    statementDateTo: String(r.statementDateTo ?? ""),
    currency: currencyCode(r.currency),
    statementBalance: money(r.statementBalance),
    systemBalance: money(r.systemBalance),
    difference: money(r.difference),
    status: r.status as ReconciliationSessionStatus,
    linesCount: count(counts.lines),
  }
}

function normalizeSessionDetail(value: unknown): ReconciliationSessionDetail {
  const r = asRecord(value)
  const account = asRecord(r.financeAccount)
  const lines = Array.isArray(r.lines) ? r.lines : []
  return {
    id: String(r.id ?? ""),
    workspaceId: String(r.workspaceId ?? ""),
    branchId: String(r.branchId ?? ""),
    financeAccountId: String(r.financeAccountId ?? ""),
    financeAccount: { id: String(account.id ?? ""), name: String(account.name ?? "") },
    statementDateFrom: String(r.statementDateFrom ?? ""),
    statementDateTo: String(r.statementDateTo ?? ""),
    currency: currencyCode(r.currency),
    statementBalance: money(r.statementBalance),
    statementOpeningBalance: r.statementOpeningBalance ? money(r.statementOpeningBalance) : null,
    openingCheck: r.openingCheck ? (r.openingCheck as ReconciliationOpeningCheck) : null,
    systemBalance: money(r.systemBalance),
    difference: money(r.difference),
    status: r.status as ReconciliationSessionStatus,
    isEditable: Boolean(r.isEditable),
    viewerActions: Array.isArray(r.viewerActions) ? (r.viewerActions as ReconciliationViewerAction[]) : [],
    createdAt: String(r.createdAt ?? ""),
    createdByUserId: (r.createdByUserId as string | null) ?? null,
    submittedByUserId: (r.submittedByUserId as string | null) ?? null,
    submittedAt: (r.submittedAt as string | null) ?? null,
    submissionNote: (r.submissionNote as string | null) ?? null,
    reviewedByUserId: (r.reviewedByUserId as string | null) ?? null,
    reviewedAt: (r.reviewedAt as string | null) ?? null,
    decisionNote: (r.decisionNote as string | null) ?? null,
    timeline: Array.isArray(r.timeline)
      ? r.timeline.map((entry) => {
          const e = asRecord(entry)
          return {
            id: String(e.id ?? ""),
            action: String(e.action ?? ""),
            at: String(e.at ?? ""),
            actorUserId: (e.actorUserId as string | null) ?? null,
            actorName: (e.actorName as string | null) ?? null,
            actorAvatarUrl: (e.actorAvatarUrl as string | null) ?? null,
            note: (e.note as string | null) ?? null,
          }
        })
      : [],
    proofFiles: Array.isArray(r.proofFiles)
      ? r.proofFiles.map((entry) => {
          const f = asRecord(entry)
          return {
            id: String(f.id ?? ""),
            url: String(f.url ?? ""),
            fileName: String(f.fileName ?? ""),
            mimeType: String(f.mimeType ?? ""),
            fileSize: count(f.fileSize),
            uploadedAt: String(f.uploadedAt ?? ""),
          }
        })
      : [],
    lines: lines.map((line) => {
      const l = asRecord(line)
      const tx = asRecord(l.transaction)
      return {
        id: String(l.id ?? ""),
        state: (l.state as ReconciliationLineState) ?? ReconciliationLineState.Outstanding,
        stateNote: (l.stateNote as string | null) ?? null,
        clearedAt: (l.clearedAt as string | null) ?? null,
        clearedByUserId: (l.clearedByUserId as string | null) ?? null,
        transaction: {
          id: String(tx.id ?? ""),
          transactionReference: (tx.transactionReference as string | null) ?? null,
          type: String(tx.type ?? ""),
          amount: money(tx.amount),
          occurredAt: String(tx.occurredAt ?? ""),
          description: (tx.description as string | null) ?? null,
          status: String(tx.status ?? ""),
          paymentMethod: (tx.paymentMethod as FinancePaymentMethod | null) ?? null,
          paymentReference: (tx.paymentReference as string | null) ?? null,
        },
      }
    }),
  }
}

async function listReconciliationSessions(
  query: ListReconciliationSessionsQuery,
): Promise<ReconciliationSessionListResponse> {
  const response = await http.get<unknown>("/finance/reconciliation/sessions", {
    params: {
      ...scopeParams(query),
      ...(query.financeAccountId ? { financeAccountId: query.financeAccountId } : {}),
      ...(query.status ? { status: query.status } : {}),
      ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
    },
  })
  const payload = asRecord(response.data)
  const items = Array.isArray(payload.items) ? payload.items : []
  return {
    items: items.map(normalizeSessionSummary),
    // ADR-0011: the queue depth is the server's number, never re-counted from the (possibly filtered) rows.
    pendingReviewCount: count(payload.pendingReviewCount),
  }
}

async function getReconciliationSession(input: { workspaceId: string; sessionId: string }): Promise<ReconciliationSessionDetail> {
  const response = await http.get<unknown>(`/finance/reconciliation/sessions/${input.sessionId}`, {
    params: { workspaceId: input.workspaceId },
  })
  return normalizeSessionDetail(response.data)
}

async function createReconciliationSession(input: CreateReconciliationSessionInput): Promise<ReconciliationSessionDetail> {
  const response = await http.post<unknown>("/finance/reconciliation/sessions", input)
  return normalizeSessionDetail(response.data)
}

async function setReconciliationLine(input: {
  workspaceId: string
  sessionId: string
  lineId: string
  state: ReconciliationLineState
  note?: string
}): Promise<ReconciliationSessionDetail> {
  const response = await http.patch<unknown>("/finance/reconciliation/lines", input)
  return normalizeSessionDetail(response.data)
}

/** "Run": re-pull the period's transactions. Adds what is missing, keeps what is already cleared. */
async function resyncReconciliationSession(input: {
  workspaceId: string
  sessionId: string
}): Promise<ReconciliationSessionDetail> {
  const response = await http.post<unknown>(`/finance/reconciliation/sessions/${input.sessionId}/resync`, {
    workspaceId: input.workspaceId,
  })
  return normalizeSessionDetail(response.data)
}

/** Attach the statement this reconciliation was prepared against. Evidence only — nothing parses it. */
async function uploadReconciliationProof(input: {
  workspaceId: string
  sessionId: string
  file: File
}): Promise<ReconciliationSessionDetail> {
  const formData = new FormData()
  formData.append("file", input.file)
  formData.append("workspaceId", input.workspaceId)
  const response = await http.post<unknown>(
    `/finance/reconciliation/sessions/${input.sessionId}/proof`,
    formData,
  )
  return normalizeSessionDetail(response.data)
}

async function deleteReconciliationProof(input: {
  workspaceId: string
  sessionId: string
  fileId: string
}): Promise<ReconciliationSessionDetail> {
  const response = await http.delete<unknown>(
    `/finance/reconciliation/sessions/${input.sessionId}/proof/${input.fileId}`,
    { params: { workspaceId: input.workspaceId } },
  )
  return normalizeSessionDetail(response.data)
}

async function deleteReconciliationSession(input: { workspaceId: string; sessionId: string }): Promise<void> {
  await http.delete<unknown>(`/finance/reconciliation/sessions/${input.sessionId}`, {
    params: { workspaceId: input.workspaceId },
  })
}

/**
 * One transition caller for all four actions — the action IS the route segment, so a new lifecycle step needs
 * no new client function and cannot be wired to the wrong endpoint.
 */
async function transitionReconciliationSession(
  action: ReconciliationViewerAction,
  input: ReconciliationTransitionInput,
): Promise<ReconciliationSessionDetail> {
  const response = await http.post<unknown>(`/finance/reconciliation/sessions/${input.sessionId}/${TRANSITION_PATH[action]}`, {
    workspaceId: input.workspaceId,
    ...(input.note ? { note: input.note } : {}),
  })
  return normalizeSessionDetail(response.data)
}

export const financeCloseTrustApi = {
  getFiscalCalendar,
  closeFiscalPeriod,
  reopenFiscalPeriod,
  updateFiscalSettings,
  getOpeningBalancesState,
  listReconciliationSessions,
  getReconciliationSession,
  createReconciliationSession,
  setReconciliationLine,
  resyncReconciliationSession,
  uploadReconciliationProof,
  deleteReconciliationProof,
  deleteReconciliationSession,
  transitionReconciliationSession,
}
